export interface Industry {
  slug: string;
  name: string;
  icon: string;
  headline: string;
  description: string;
  painPoints: { title: string; description: string }[];
  stats: { number: string; label: string }[];
  sampleFields: string[];
  relatedSlugs: string[];
}

export const INDUSTRIES: Industry[] = [
  {
    slug: 'restaurants-hospitality',
    name: 'Restaurants & Hospitality',
    icon: '🍽️',
    headline: 'Hire Servers, Cooks & Bartenders Faster with iHireFast',
    description:
      'Restaurants and hospitality businesses face constant turnover. iHireFast helps you fill open positions for servers, line cooks, bartenders, hosts, and managers in days instead of weeks so you never run short-staffed again.',
    painPoints: [
      {
        title: 'High turnover drains your time',
        description:
          'The restaurant industry sees annual turnover rates above 70%. You need a system that keeps your hiring pipeline full without consuming your day.',
      },
      {
        title: 'Candidates ghost interviews',
        description:
          'When applicants wait too long to hear back, they move on. Instant auto-replies and self-scheduling keep candidates engaged.',
      },
      {
        title: 'Managing applications across job boards is chaotic',
        description:
          'Applications come from Indeed, Craigslist, walk-ins, and word of mouth. iHireFast centralizes them all in one dashboard.',
      },
    ],
    stats: [
      { number: '73%', label: 'Industry turnover rate' },
      { number: '3 days', label: 'Avg. time to first interview' },
      { number: '2x', label: 'Faster hiring with iHireFast' },
      { number: '94%', label: 'Auto-reply response rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position Applied For (Dropdown: Server, Cook, Bartender, Host, Manager)',
      'Years of Restaurant Experience',
      'Availability (Dropdown: Full-Time, Part-Time, Weekends Only)',
      'Food Handler Certification (Yes/No)',
      'Resume Upload',
    ],
    relatedSlugs: ['retail-sales', 'fitness-wellness', 'salon-beauty'],
  },
  {
    slug: 'healthcare-medical',
    name: 'Healthcare & Medical',
    icon: '🏥',
    headline: 'Hire Nurses, Medical Assistants & Admin Staff Faster',
    description:
      'Healthcare facilities cannot afford staffing gaps. iHireFast helps clinics, hospitals, and private practices hire nurses, medical assistants, front desk staff, and admin professionals quickly and compliantly.',
    painPoints: [
      {
        title: 'Staffing shortages delay patient care',
        description:
          'Unfilled positions mean longer wait times and burned-out staff. Fast hiring directly impacts patient outcomes.',
      },
      {
        title: 'Credential verification slows the process',
        description:
          'You need to collect certifications, licenses, and references early. Custom forms let you capture this on day one.',
      },
      {
        title: 'Coordinating interview schedules is a nightmare',
        description:
          'Doctors, office managers, and HR all need to be involved. Google Calendar integration keeps everyone aligned.',
      },
    ],
    stats: [
      { number: '45 days', label: 'Typical healthcare time-to-fill' },
      { number: '60%', label: 'Reduction with iHireFast' },
      { number: '500+', label: 'Medical practices served' },
      { number: '98%', label: 'Candidate satisfaction rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position Applied For (Dropdown: RN, LPN, Medical Assistant, Front Desk, Admin)',
      'License/Certification Number',
      'Years of Healthcare Experience',
      'CPR Certified (Yes/No)',
      'Resume Upload',
      'References (Text Area)',
    ],
    relatedSlugs: ['education', 'fitness-wellness', 'insurance-finance'],
  },
  {
    slug: 'construction-trades',
    name: 'Construction & Trades',
    icon: '🔨',
    headline: 'Hire Electricians, Plumbers & Laborers Faster',
    description:
      'Construction companies and trade businesses need skilled workers fast. iHireFast helps you collect applications from electricians, plumbers, laborers, and foremen, then schedule interviews without the phone tag.',
    painPoints: [
      {
        title: 'Skilled labor is hard to find',
        description:
          'The trades face a massive labor shortage. When a qualified candidate applies, you need to respond within minutes, not days.',
      },
      {
        title: 'Job site schedules make coordination difficult',
        description:
          'Foremen and project managers are on-site all day. Self-scheduling booking pages let candidates pick interview times that work.',
      },
      {
        title: 'Paper applications get lost',
        description:
          'Walk-in applications on clipboards disappear. Digital forms keep everything organized and searchable.',
      },
    ],
    stats: [
      { number: '650K', label: 'Unfilled construction jobs in the US' },
      { number: '48hrs', label: 'Avg. time to first contact' },
      { number: '3x', label: 'More applicants with digital forms' },
      { number: '87%', label: 'Interview show-up rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Trade/Specialty (Dropdown: Electrician, Plumber, Carpenter, Laborer, Foreman)',
      'Years of Experience',
      'OSHA Certification (Yes/No)',
      'Valid Driver\'s License (Yes/No)',
      'Available Start Date',
      'Resume Upload',
    ],
    relatedSlugs: ['automotive', 'cleaning-janitorial', 'logistics-warehousing'],
  },
  {
    slug: 'retail-sales',
    name: 'Retail & Sales',
    icon: '🛍️',
    headline: 'Hire Store Associates, Cashiers & Managers Faster',
    description:
      'Retail stores need reliable staff ready to start quickly. iHireFast helps you hire store associates, cashiers, shift leads, and managers by automating applications, communication, and interview scheduling.',
    painPoints: [
      {
        title: 'Seasonal hiring spikes overwhelm your team',
        description:
          'Holiday and back-to-school rushes mean dozens of openings at once. Automated workflows handle the volume without extra HR hours.',
      },
      {
        title: 'High no-show rates for interviews',
        description:
          'Calendar invites and automated reminders cut no-shows dramatically by keeping candidates committed.',
      },
      {
        title: 'Multiple locations multiply the chaos',
        description:
          'Create separate forms per store location and track applicants independently, all from one dashboard.',
      },
    ],
    stats: [
      { number: '60%', label: 'Retail turnover rate' },
      { number: '5 days', label: 'Avg. time to hire' },
      { number: '40%', label: 'Fewer no-shows with reminders' },
      { number: '200+', label: 'Retail businesses on iHireFast' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Sales Associate, Cashier, Shift Lead, Store Manager)',
      'Preferred Store Location',
      'Availability (Checkboxes: Mornings, Afternoons, Evenings, Weekends)',
      'Previous Retail Experience (Years)',
      'Resume Upload',
    ],
    relatedSlugs: ['restaurants-hospitality', 'salon-beauty', 'fitness-wellness'],
  },
  {
    slug: 'technology-it',
    name: 'Technology & IT',
    icon: '💻',
    headline: 'Hire Developers, IT Support & Project Managers Faster',
    description:
      'Tech companies compete for top talent in a fast-moving market. iHireFast gives you branded application forms, structured pipelines, and automated scheduling so you can move quickly and land the best candidates.',
    painPoints: [
      {
        title: 'Top candidates disappear fast',
        description:
          'In tech, the best applicants are off the market in days. Instant notifications and fast scheduling keep you competitive.',
      },
      {
        title: 'Technical screening adds steps',
        description:
          'Use custom form fields to collect GitHub profiles, portfolio links, and technical assessment responses upfront.',
      },
      {
        title: 'Coordinating panel interviews is time-consuming',
        description:
          'Google Calendar integration checks availability across multiple interviewers so candidates see only open slots.',
      },
    ],
    stats: [
      { number: '36 days', label: 'Avg. tech time-to-fill' },
      { number: '50%', label: 'Reduction with iHireFast' },
      { number: '85%', label: 'Offer acceptance rate' },
      { number: '10K+', label: 'Tech applicants processed' },
    ],
    sampleFields: [
      'Full Name',
      'Email Address',
      'Phone Number',
      'Position (Dropdown: Frontend Developer, Backend Developer, IT Support, Project Manager)',
      'LinkedIn Profile URL',
      'GitHub/Portfolio URL',
      'Years of Experience',
      'Tech Stack (Text Area)',
      'Resume Upload',
    ],
    relatedSlugs: ['real-estate', 'insurance-finance', 'education'],
  },
  {
    slug: 'real-estate',
    name: 'Real Estate',
    icon: '🏠',
    headline: 'Hire Agents, Property Managers & Leasing Consultants Faster',
    description:
      'Real estate brokerages and property management companies need agents and staff who can hit the ground running. iHireFast streamlines hiring for agents, property managers, leasing consultants, and admin staff.',
    painPoints: [
      {
        title: 'Licensed agents are in high demand',
        description:
          'Competing brokerages recruit the same agents. Respond to applicants within minutes to stand out.',
      },
      {
        title: 'Collecting license info manually is tedious',
        description:
          'Custom form fields let you capture real estate license numbers, MLS IDs, and certifications at the point of application.',
      },
      {
        title: 'Agents have unpredictable schedules',
        description:
          'Self-scheduling booking pages let candidates pick interview times around showings and closings.',
      },
    ],
    stats: [
      { number: '14 days', label: 'Avg. time to hire an agent' },
      { number: '3x', label: 'More applicants via digital forms' },
      { number: '92%', label: 'Candidate engagement rate' },
      { number: '300+', label: 'Brokerages using iHireFast' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Real Estate Agent, Property Manager, Leasing Consultant, Admin)',
      'Real Estate License Number',
      'Years in Real Estate',
      'MLS ID (if applicable)',
      'Resume Upload',
    ],
    relatedSlugs: ['insurance-finance', 'technology-it', 'legal-law-firms'],
  },
  {
    slug: 'legal-law-firms',
    name: 'Legal & Law Firms',
    icon: '⚖️',
    headline: 'Hire Paralegals, Legal Assistants & Attorneys Faster',
    description:
      'Law firms need qualified staff who understand the legal profession. iHireFast helps firms hire paralegals, legal assistants, attorneys, and office staff with structured application forms and organized tracking.',
    painPoints: [
      {
        title: 'Credential verification is critical',
        description:
          'Bar admissions, paralegal certifications, and references must be collected early. Custom forms capture everything upfront.',
      },
      {
        title: 'Partners have packed schedules',
        description:
          'Google Calendar integration finds open slots across multiple partners so interviews get booked without the back-and-forth.',
      },
      {
        title: 'Confidentiality matters from day one',
        description:
          'All data is encrypted and stored securely. Candidate information never leaks outside your workspace.',
      },
    ],
    stats: [
      { number: '30 days', label: 'Avg. legal time-to-fill' },
      { number: '55%', label: 'Faster with iHireFast' },
      { number: '100+', label: 'Law firms served' },
      { number: '96%', label: 'Data security uptime' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Attorney, Paralegal, Legal Assistant, Office Manager)',
      'Bar Admission State (if applicable)',
      'Years of Legal Experience',
      'Practice Areas of Interest (Checkboxes)',
      'Resume Upload',
      'Writing Sample Upload',
    ],
    relatedSlugs: ['real-estate', 'insurance-finance', 'healthcare-medical'],
  },
  {
    slug: 'education',
    name: 'Education',
    icon: '📚',
    headline: 'Hire Teachers, Tutors & Admin Staff Faster',
    description:
      'Schools, tutoring centers, and educational organizations need dedicated educators and support staff. iHireFast helps you find and hire teachers, tutors, counselors, and administrators efficiently.',
    painPoints: [
      {
        title: 'Hiring cycles are seasonal and urgent',
        description:
          'Schools hire heavily before fall and after winter break. Automating the pipeline ensures you fill positions before the semester starts.',
      },
      {
        title: 'Background checks and certifications add complexity',
        description:
          'Collect teaching certifications, background check consent, and references directly on the application form.',
      },
      {
        title: 'Committee-based hiring slows decisions',
        description:
          'Team collaboration features let principals, department heads, and HR review candidates and leave notes in one place.',
      },
    ],
    stats: [
      { number: '25 days', label: 'Avg. education time-to-fill' },
      { number: '45%', label: 'Faster with iHireFast' },
      { number: '150+', label: 'Schools and centers served' },
      { number: '90%', label: 'Interview completion rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Teacher, Tutor, Counselor, Admin, Substitute)',
      'Subject Area / Grade Level',
      'Teaching Certification Type',
      'Years of Teaching Experience',
      'Resume Upload',
      'Background Check Consent (Checkbox)',
    ],
    relatedSlugs: ['healthcare-medical', 'fitness-wellness', 'technology-it'],
  },
  {
    slug: 'fitness-wellness',
    name: 'Fitness & Wellness',
    icon: '💪',
    headline: 'Hire Trainers, Front Desk Staff & Yoga Instructors Faster',
    description:
      'Gyms, yoga studios, and wellness centers thrive on their people. iHireFast helps you find and hire personal trainers, group fitness instructors, front desk staff, and wellness coaches quickly.',
    painPoints: [
      {
        title: 'Certified trainers are always in demand',
        description:
          'When a great trainer applies, you need to act fast. Instant notifications and auto-replies keep them engaged before a competitor does.',
      },
      {
        title: 'Class schedules make interview coordination tough',
        description:
          'Booking pages let candidates schedule around class times so neither party has to juggle calendars manually.',
      },
      {
        title: 'Personality matters as much as qualifications',
        description:
          'Video upload fields let candidates introduce themselves before the in-person interview, saving time for everyone.',
      },
    ],
    stats: [
      { number: '18%', label: 'Annual trainer turnover' },
      { number: '4 days', label: 'Avg. time to first interview' },
      { number: '75%', label: 'Video intro submission rate' },
      { number: '200+', label: 'Fitness businesses served' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Personal Trainer, Group Instructor, Front Desk, Yoga Instructor)',
      'Certifications (Checkboxes: ACE, NASM, ISSA, RYT-200, Other)',
      'Specialty Areas',
      'Years of Experience',
      'Video Introduction Upload',
      'Resume Upload',
    ],
    relatedSlugs: ['salon-beauty', 'healthcare-medical', 'restaurants-hospitality'],
  },
  {
    slug: 'automotive',
    name: 'Automotive',
    icon: '🚗',
    headline: 'Hire Mechanics, Service Advisors & Detailers Faster',
    description:
      'Auto shops, dealerships, and detailing businesses need skilled technicians and customer-facing staff. iHireFast helps you hire mechanics, service advisors, detailers, and parts specialists with streamlined applications.',
    painPoints: [
      {
        title: 'Certified technicians are scarce',
        description:
          'ASE-certified mechanics are in high demand. Respond to qualified applicants instantly to lock them in before your competition.',
      },
      {
        title: 'Shop hours leave little time for HR',
        description:
          'Automated workflows handle applications, emails, and scheduling so you can focus on the shop floor.',
      },
      {
        title: 'Walk-in applicants are hard to track',
        description:
          'Give walk-ins a QR code to your digital application form and everything flows into your dashboard automatically.',
      },
    ],
    stats: [
      { number: '76K', label: 'Unfilled auto tech positions' },
      { number: '5 days', label: 'Avg. time to hire' },
      { number: '2x', label: 'More applicants with digital forms' },
      { number: '91%', label: 'Candidate response rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Mechanic, Service Advisor, Detailer, Parts Specialist)',
      'ASE Certifications (Checkboxes)',
      'Years of Automotive Experience',
      'Valid Driver\'s License (Yes/No)',
      'Resume Upload',
    ],
    relatedSlugs: ['construction-trades', 'logistics-warehousing', 'cleaning-janitorial'],
  },
  {
    slug: 'cleaning-janitorial',
    name: 'Cleaning & Janitorial',
    icon: '🧹',
    headline: 'Hire Cleaners, Supervisors & Cleaning Specialists Faster',
    description:
      'Cleaning companies and janitorial services operate on tight margins and need reliable staff. iHireFast helps you hire cleaners, supervisors, and specialists quickly so you can take on more contracts.',
    painPoints: [
      {
        title: 'Turnover is constant',
        description:
          'The cleaning industry has some of the highest turnover rates. A constant pipeline of applicants keeps you staffed.',
      },
      {
        title: 'Language barriers complicate communication',
        description:
          'Simple, visual application forms work for applicants of all language backgrounds, reducing confusion.',
      },
      {
        title: 'Scheduling across multiple job sites is complex',
        description:
          'Calendar integration helps you schedule interviews around your client site assignments.',
      },
    ],
    stats: [
      { number: '100%+', label: 'Annual industry turnover' },
      { number: '2 days', label: 'Avg. time to hire' },
      { number: '3x', label: 'Larger applicant pool' },
      { number: '85%', label: 'Interview show-up rate' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Cleaner, Supervisor, Floor Specialist, Window Cleaner)',
      'Available Shifts (Checkboxes: Day, Evening, Night, Weekends)',
      'Own Transportation (Yes/No)',
      'Years of Cleaning Experience',
      'Background Check Consent (Checkbox)',
    ],
    relatedSlugs: ['construction-trades', 'logistics-warehousing', 'pet-services'],
  },
  {
    slug: 'logistics-warehousing',
    name: 'Logistics & Warehousing',
    icon: '📦',
    headline: 'Hire Warehouse Workers, Drivers & Dispatchers Faster',
    description:
      'Logistics companies and warehouses need to scale staff quickly for peak seasons. iHireFast helps you hire warehouse workers, delivery drivers, dispatchers, and inventory specialists efficiently.',
    painPoints: [
      {
        title: 'Peak season hiring demands are massive',
        description:
          'Holiday and prime day rushes mean hiring hundreds of workers fast. Automated forms and bulk email tools handle the volume.',
      },
      {
        title: 'CDL and certification verification takes time',
        description:
          'Collect CDL numbers, forklift certifications, and safety training records directly on the application form.',
      },
      {
        title: 'Shift coverage gaps cost money',
        description:
          'Fast hiring means fewer missed shifts and less overtime for existing staff.',
      },
    ],
    stats: [
      { number: '300%', label: 'Seasonal hiring increase' },
      { number: '3 days', label: 'Avg. time to hire' },
      { number: '50%', label: 'Less time on admin tasks' },
      { number: '400+', label: 'Logistics companies served' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Warehouse Associate, Driver, Dispatcher, Inventory Specialist)',
      'CDL Type (if applicable)',
      'Forklift Certified (Yes/No)',
      'Available Shifts (Checkboxes: Day, Evening, Night, Overnight)',
      'Years of Warehouse/Logistics Experience',
      'Resume Upload',
    ],
    relatedSlugs: ['construction-trades', 'automotive', 'cleaning-janitorial'],
  },
  {
    slug: 'salon-beauty',
    name: 'Salon & Beauty',
    icon: '💇',
    headline: 'Hire Stylists, Estheticians & Receptionists Faster',
    description:
      'Salons, spas, and beauty studios depend on talented professionals. iHireFast helps you find and hire hairstylists, estheticians, nail techs, and front desk receptionists with branded forms that reflect your studio.',
    painPoints: [
      {
        title: 'Portfolio review is essential',
        description:
          'File upload fields let candidates share portfolio photos and videos of their work before you even schedule an interview.',
      },
      {
        title: 'Stylists get recruited constantly',
        description:
          'Speed matters. Auto-reply emails and instant scheduling keep top stylists engaged with your salon, not a competitor.',
      },
      {
        title: 'Branding matters in beauty',
        description:
          'Custom branded forms and emails give candidates a polished first impression that reflects your studio\'s aesthetic.',
      },
    ],
    stats: [
      { number: '22%', label: 'Annual stylist turnover' },
      { number: '4 days', label: 'Avg. time to hire' },
      { number: '80%', label: 'Portfolio submission rate' },
      { number: '150+', label: 'Salons on iHireFast' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Hairstylist, Esthetician, Nail Tech, Receptionist)',
      'Cosmetology License Number',
      'Years of Experience',
      'Specialties (Checkboxes: Color, Cuts, Extensions, Facials, Waxing)',
      'Portfolio Upload (Photos/Video)',
      'Resume Upload',
    ],
    relatedSlugs: ['fitness-wellness', 'restaurants-hospitality', 'retail-sales'],
  },
  {
    slug: 'insurance-finance',
    name: 'Insurance & Finance',
    icon: '💼',
    headline: 'Hire Agents, Underwriters & Claims Adjusters Faster',
    description:
      'Insurance agencies and financial services firms need licensed professionals and detail-oriented staff. iHireFast helps you hire agents, underwriters, claims adjusters, and analysts with structured application forms.',
    painPoints: [
      {
        title: 'Licensing requirements vary by state',
        description:
          'Custom form fields collect license types, numbers, and states of licensure so you can verify credentials before the first interview.',
      },
      {
        title: 'Compliance adds hiring friction',
        description:
          'Capture consent forms, background check authorization, and compliance disclosures directly in the application.',
      },
      {
        title: 'Experienced agents get multiple offers',
        description:
          'Fast response times and professional communication keep your agency top-of-mind for in-demand talent.',
      },
    ],
    stats: [
      { number: '400K', label: 'Insurance job openings annually' },
      { number: '7 days', label: 'Avg. time to hire' },
      { number: '65%', label: 'Faster with iHireFast' },
      { number: '250+', label: 'Agencies served' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Insurance Agent, Underwriter, Claims Adjuster, Financial Analyst)',
      'License Type and Number',
      'State(s) of Licensure',
      'Years of Industry Experience',
      'Background Check Consent (Checkbox)',
      'Resume Upload',
    ],
    relatedSlugs: ['real-estate', 'legal-law-firms', 'technology-it'],
  },
  {
    slug: 'pet-services',
    name: 'Pet Services',
    icon: '🐾',
    headline: 'Hire Groomers, Vet Techs & Kennel Attendants Faster',
    description:
      'Pet care businesses need staff who love animals and are reliable. iHireFast helps groomers, veterinary clinics, kennels, and pet daycares hire vet techs, groomers, kennel attendants, and receptionists faster.',
    painPoints: [
      {
        title: 'Animal handling experience is non-negotiable',
        description:
          'Custom form questions screen for experience with different animal types, sizes, and temperaments before you invest in an interview.',
      },
      {
        title: 'Early morning and weekend shifts are hard to fill',
        description:
          'Availability checkboxes on your application form filter out candidates who cannot work the hours you need.',
      },
      {
        title: 'Certifications matter for medical roles',
        description:
          'Collect veterinary technician licenses, grooming certifications, and first aid training on the application form.',
      },
    ],
    stats: [
      { number: '$150B', label: 'US pet industry size' },
      { number: '3 days', label: 'Avg. time to hire' },
      { number: '2x', label: 'More qualified applicants' },
      { number: '100+', label: 'Pet businesses served' },
    ],
    sampleFields: [
      'Full Name',
      'Phone Number',
      'Email Address',
      'Position (Dropdown: Groomer, Vet Tech, Kennel Attendant, Receptionist)',
      'Animal Handling Experience (Years)',
      'Certifications (Checkboxes: Vet Tech License, Grooming Cert, Pet First Aid)',
      'Comfortable With Large Dogs (Yes/No)',
      'Available Shifts (Checkboxes: Mornings, Afternoons, Evenings, Weekends)',
      'Resume Upload',
    ],
    relatedSlugs: ['cleaning-janitorial', 'fitness-wellness', 'salon-beauty'],
  },
];

export function getIndustryBySlug(slug: string): Industry | undefined {
  return INDUSTRIES.find((i) => i.slug === slug);
}
